import React, { useState } from "react"
import {
  Table,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import moment from "moment"
import {
  deleteEmailReportConfig,
  resendEmailReport,
} from "../../helpers/email_report_helper"
import toastr from "toastr"

const EmailReportList = ({ configs, onEdit, onRefresh }) => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [resending, setResending] = useState(null)

  const handleDelete = async () => {
    if (!selectedConfig) return

    try {
      await deleteEmailReportConfig(selectedConfig.company_id._id)
      toastr.success("Email report configuration deleted successfully")
      setDeleteModal(false)
      setSelectedConfig(null)
      onRefresh()
    } catch (error) {
      console.error("Error deleting config:", error)
      toastr.error("Failed to delete configuration")
    }
  }

  const handleResend = async (config) => {
    try {
      setResending(config._id)
      await resendEmailReport(config.company_id._id)
      toastr.success("Report sent successfully!")
    } catch (error) {
      console.error("Error resending report:", error)
      toastr.error("Failed to send report")
    } finally {
      setResending(null)
    }
  }

  const openDeleteModal = (config) => {
    setSelectedConfig(config)
    setDeleteModal(true)
  }

  return (
    <>
      <div className="table-responsive">
        <Table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Company</th>
              <th>Email Recipients</th>
              <th>Schedule</th>
              <th>Status</th>
              <th>Last Sent</th>
              <th>Next Scheduled</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.map((config) => (
              <tr key={config._id}>
                <td>
                  <div>
                    <strong>{config.company_name}</strong>
                    {config.company_address && (
                      <div className="text-muted small">
                        {config.company_address}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div>
                    {config.email_recipients.map((email, index) => (
                      <div key={index} className="small">
                        <i className="bx bx-envelope me-1"></i>
                        {email}
                      </div>
                    ))}
                  </div>
                </td>
                <td>
                  <Badge color="info" className="me-1">
                    {config.schedule_frequency}
                  </Badge>
                  <div className="text-muted small">at {config.schedule_time}</div>
                </td>
                <td>
                  {config.enabled ? (
                    <Badge color="success">
                      <i className="bx bx-check-circle me-1"></i>
                      Enabled
                    </Badge>
                  ) : (
                    <Badge color="secondary">
                      <i className="bx bx-x-circle me-1"></i>
                      Disabled
                    </Badge>
                  )}
                </td>
                <td>
                  {config.last_sent_at ? (
                    <span className="small">
                      {moment(config.last_sent_at).format("MMM DD, YYYY HH:mm")}
                    </span>
                  ) : (
                    <span className="text-muted small">Never sent</span>
                  )}
                </td>
                <td>
                  {config.enabled && config.next_scheduled_at ? (
                    <span className="small">
                      {moment(config.next_scheduled_at).format("MMM DD, YYYY HH:mm")}
                    </span>
                  ) : (
                    <span className="text-muted small">-</span>
                  )}
                </td>
                <td className="text-center">
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="button"
                      className="btn btn-sm btn-light"
                    >
                      <i className="bx bx-dots-vertical-rounded"></i>
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem onClick={() => onEdit(config)}>
                        <i className="bx bx-edit me-2"></i>
                        Edit
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => handleResend(config)}
                        disabled={resending === config._id}
                      >
                        <i className="bx bx-send me-2"></i>
                        {resending === config._id ? "Sending..." : "Send Now"}
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem
                        onClick={() => openDeleteModal(config)}
                        className="text-danger"
                      >
                        <i className="bx bx-trash me-2"></i>
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the email report configuration for{" "}
          <strong>{selectedConfig?.company_name}</strong>?
          <div className="alert alert-warning mt-3 mb-0">
            <i className="bx bx-info-circle me-2"></i>
            This action cannot be undone and scheduled reports will stop.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default EmailReportList
